// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = Partial<WidgetProps> & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
    badgeContainer?: InjectableCss;
    badgeMessage?: TypographyPresentationProps;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
            margin-bottom: 22px;
            border-bottom: 1px solid ${getColor("common.ui2")};
        `,
    },
    badgesWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
        `,
    },
    badgeContainer: {
        css: css`
            min-width: 50%;
            display: flex;
            align-items: center;
            margin-bottom: 13px;
        `,
    },
    badgeMessage: {
        size: "14px",
        lineHeight: "19px",
        css: css`
            font-weight: 500;
        `,
    },
};

const styles = descriptionStyles;

const ProductDetailsDescription: React.FC<Props> = ({ product }) => {
    if (!product) {
        return <></>;
    }

    const isWarranty = product.properties?.warranty?.length > 0;
    const isEco = product.properties?.hasEcoFee?.toLowerCase() === "true";
    const hasIncludes = product.properties?.includes?.length > 0;
    const application = product.properties?.application?.length > 0;
    const hasShortDescription = product.properties?.shortDescription?.length > 0;
    const standardsApprovals = product.properties?.standardsApprovals?.length > 0;

    if (!hasShortDescription && !isWarranty && !isEco && !hasIncludes && !application && !standardsApprovals) {
        return <></>;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {hasShortDescription && (
                <StyledWrapper
                    css={css`
                        margin-bottom: 15px;
                        font-weight: 500;
                    `}
                >
                    <div data-test-selector="productDetails_htmlContent">
                        {parse(transformContentHtml(product.properties?.shortDescription), parserOptions)}
                    </div>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.badgesWrapper}>
                {isWarranty && (
                    <StyledWrapper {...styles.badgeContainer}>
                        <Icon
                            src="Warranty"
                            size={20}
                            css={css`
                                margin-right: 6px;
                            `}
                        />
                        <Typography {...styles.badgeMessage}>
                            {parse(transformContentHtml(product.properties?.warranty), parserOptions)}
                        </Typography>
                    </StyledWrapper>
                )}
                {isEco && (
                    <StyledWrapper {...styles.badgeContainer}>
                        <Icon
                            src="Eco"
                            size={20}
                            color="success.main"
                            css={css`
                                margin-right: 6px;
                            `}
                        />
                        <Typography {...styles.badgeMessage}>{siteMessage("Custom_ProductEcoFeeMessage")}</Typography>
                    </StyledWrapper>
                )}
                {hasIncludes && (
                    <StyledWrapper {...styles.badgeContainer}>
                        <Icon
                            src="Box"
                            size={20}
                            color="text.main"
                            css={css`
                                margin-right: 6px;
                            `}
                        />
                        <Typography {...styles.badgeMessage}>
                            {parse(product.properties?.includes, parserOptions)}
                        </Typography>
                    </StyledWrapper>
                )}
                {application && (
                    <StyledWrapper {...styles.badgeContainer}>
                        <Icon
                            src="Applications"
                            size={23}
                            color="info.main"
                            css={css`
                                margin-right: 6px;
                            `}
                        />
                        <Typography {...styles.badgeMessage}>
                            {parse(product.properties?.application, parserOptions)}
                        </Typography>
                    </StyledWrapper>
                )}
                {standardsApprovals && (
                    <StyledWrapper {...styles.badgeContainer}>
                        <Icon
                            src="Standards"
                            size={23}
                            color="text.main"
                            css={css`
                                margin-right: 6px;
                            `}
                        />
                        {/* TODO: Remove Parse, we don't need it by DB values */}
                        <Typography {...styles.badgeMessage}>
                            {parse(product.properties?.standardsApprovals, parserOptions)}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const ProductDetailsDescriptionComnponent: React.FC<Props> = ProductDetailsDescription;

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDescription),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
